import { Courier, Trip } from '../../store/config/types';
import { Button, IconButton, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import moment from 'moment-timezone';
import {
  MoreVert,
  Cancel,
  CheckCircle,
  DirectionsBus,
  HourglassEmpty,
  NewReleases,
  Visibility,
} from '@material-ui/icons';
import { green, grey } from '@mui/material/colors';
import { getVehicleDisplayName } from '../../utils/string.helpers';
import PieProgress from '../PieProgress/PieProgress';

interface TripViewTripActionsProps {
  trip: Trip;
  couriers?: Courier[];
  timezone?: string;
  canComputeStops?: boolean;
  visible?: boolean;
  /* eslint-disable */
  onMenuSelect?: (tripId: number | undefined, menuAction: string) => void;
  onVisibilityChange?: (tripId: number, value: boolean) => void;
  /* eslint-enable */
}
const TripStatusIcons: any = {
  NEW: <NewReleases style={{ color: green[200], width: 16, height: 16 }} />,
  PENDING: <HourglassEmpty style={{ color: grey[500], width: 16, height: 16 }} />,
  CANCELLED: <Cancel style={{ color: grey[400], width: 16, height: 16 }} />,
  IN_TRANSIT: <DirectionsBus style={{ color: green[400], width: 16, height: 16 }} />,
  FINISHED: <CheckCircle style={{ color: grey[600], width: 16, height: 16 }} />,
};

export default function TripViewTripActions({
  trip,
  couriers,
  timezone,
  canComputeStops,
  visible,
  onMenuSelect,
  onVisibilityChange,
}: TripViewTripActionsProps) {
  const [moreMenuElement, setMoreMenuElement] = useState<HTMLElement | null>(null);

  timezone && moment.tz.setDefault(timezone);

  const handleMoreMenuClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    setMoreMenuElement(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMoreMenuElement(null);
  };
  const handleMenuSelect = (action: string) => {
    onMenuSelect && onMenuSelect(trip.tripId, action);
    handleMenuClose();
  };
  const courierId = trip.tripOffers.find((offer) => ['PENDING', 'ACCEPTED'].includes(offer.status))?.courierId;
  const courier = couriers?.find((courierItem) => courierItem.courierId === courierId);
  //couriers && trip.tripOffers[0] ? couriers.find((c) => c.courierId === trip.tripOffers[0].courierId) : undefined;
  const courierName = courier
    ? `${courier.firstName} ${courier.lastName}`
    : trip.dspJob
    ? `${trip.dspJob.deliveryServiceProvider?.displayName}` +
      (trip.dspJob?.supportReference ? ` (${trip.dspJob.supportReference})` : '')
    : //(trip.dspJob.courier.firstName ? `(${trip.dspJob.courier.firstName} ${trip.dspJob.courier.lastName})` : '')
      undefined;
  const tripDeletable =
    ['NEW', 'PENDING', 'CANCELLED'].includes(trip.status) &&
    !trip.dspJob &&
    !(trip.startsAt && moment(trip.startsAt).isSame(moment(), 'day'));
  // const isNew = ['NEW'].includes(trip.status);
  const statusLabel = trip.status
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/^./, (c) => c.toUpperCase());
  const warehouseStop = trip.tripStops[0];
  const utilization = trip.vehicle?.capacity
    ? ((warehouseStop?.packageCount ?? warehouseStop?.stopPackages?.length ?? 0) / (trip.vehicle.capacity ?? 1)) * 100
    : 0;
  return (
    <div className={`courier-area ${trip.status}`}>
      <Button
        variant="outlined"
        size="small"
        className="trip-details-button"
        onClick={() => {
          handleMenuSelect('details');
        }}
      >
        Details
      </Button>
      {visible !== undefined && (
        <span className="visible-on-map">
          <IconButton
            size="small"
            onClick={() => {
              onVisibilityChange && onVisibilityChange(trip.tripId ?? 0, !visible);
            }}
          >
            <Visibility style={{ color: visible ? 'GrayText' : 'lightgray', width: 20 }} />
          </IconButton>
        </span>
      )}
      <span className="trip-menu">
        <IconButton size="small" onClick={handleMoreMenuClick}>
          <MoreVert />
        </IconButton>
        <Menu
          id="trips-menu"
          className="trip-popup-menu"
          anchorEl={moreMenuElement}
          open={moreMenuElement ? true : false}
          onClose={handleMenuClose}
        >
          <span className="menu-title">{`Trip #${trip.tripId}`}</span>
          <MenuItem
            key={`trip-details`}
            onClick={() => {
              handleMenuSelect('details');
            }}
          >
            <span>Details</span>
          </MenuItem>
          {/*isNew && courier && (
            <MenuItem
              key={`trip-remove-offer`}
              onClick={() => {
                handleMenuSelect('remove-offer');
              }}
            >
              <span>Remove Offer</span>
            </MenuItem>
          )*/}
          {/*!trip.dspJob && isNew && !courier && (
            <MenuItem
              key={`trip-offer`}
              onClick={() => {
                handleMenuSelect('offer');
              }}
            >
              <span>Offer to</span>
            </MenuItem>
          )*/}
          {trip.dspJob && trip.status === 'NEW' && (
            <MenuItem
              key={`cancel-third-party`}
              onClick={() => {
                handleMenuSelect('cancel-third-party');
              }}
            >
              <span>Cancel Third-party</span>
            </MenuItem>
          )}
          {trip.dspJob && (
            <MenuItem
              key={`third-party-support`}
              onClick={() => {
                handleMenuSelect('third-party-support');
              }}
            >
              <span>Need Support?</span>
            </MenuItem>
          )}
          {tripDeletable && (
            <MenuItem
              key={`trip-delete`}
              onClick={() => {
                handleMenuSelect('delete');
              }}
            >
              <span>Delete</span>
            </MenuItem>
          )}
          {canComputeStops && (
            <MenuItem
              key={`trip-compute`}
              onClick={() => {
                handleMenuSelect('compute-stops');
              }}
            >
              <span>Compute Stops</span>
            </MenuItem>
          )}
        </Menu>
      </span>
      <div className="trip-info">
        <div className="trip-status" title={statusLabel}>
          {TripStatusIcons[trip.status] ?? ''}
          <PieProgress
            value={utilization}
            size={12}
            color="#4CAF50"
            title={`${warehouseStop?.packageCount ?? warehouseStop?.stopPackages?.length ?? 0} / ${
              trip.vehicle?.capacity ?? 1
            } Packages`}
          />
          {getVehicleDisplayName(trip.vehicle)}
        </div>
        <div className={`courier-label${courier ? '' : ' empty'} ${trip.status}`}>{courierName ?? 'Not Offered'}</div>
      </div>
    </div>
  );
}
